import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, LayoutDashboard, GraduationCap, Users, UserCog, Settings2, DollarSign, BarChart3, BookOpen, Shield, Building2, CalendarCheck, Heart, User, CalendarDays, Clock, Library, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useApp } from '../../contexts/AppContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export const navIconMap = {
  'dashboard': LayoutDashboard,
  'parent-portal': Heart,
  'my-profile': User,
  'attendance': CalendarCheck,
  'learning': GraduationCap,
  'students': Users,
  'teachers': UserCog,
  'leave-management': Clock,
  'teacher-schedule': CalendarDays,
  'operations': Settings2,
  'finance': DollarSign,
  'analytics': BarChart3,
  'library': Library,
  'administration': Shield,
  'school-management': Building2,
};

/**
 * Two visual modes driven by one `<Sidebar />`:
 *   • Desktop (≥lg) — fixed left rail. Width animates 80↔256 via
 *     `sidebarCollapsed`. Hidden as a rail below lg so it doesn't push
 *     main content.
 *   • Mobile (<lg) — full-height slide-out drawer with backdrop.
 *     Controlled by `mobileDrawerOpen`. Auto-closes on route change &
 *     on backdrop click. Locks body scroll while open.
 *
 * Both modes share the same nav list so we never get drift.
 */
export function Sidebar() {
  const {
    currentSchool,
    sidebarCollapsed,
    toggleSidebar,
    filteredNavigation,
    mobileDrawerOpen,
    closeMobileDrawer,
  } = useApp();
  const location = useLocation();

  // Auto-close drawer when user navigates so the next page isn't covered.
  useEffect(() => { closeMobileDrawer(); }, [location.pathname, closeMobileDrawer]);

  // Body scroll lock while drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (mobileDrawerOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [mobileDrawerOpen]);

  const NavList = ({ collapsed = false, onItemClick }) => (
    <nav className="flex-1 py-4 px-3 overflow-y-auto scrollbar-thin">
      <TooltipProvider delayDuration={0}>
        <ul className="space-y-1">
          {filteredNavigation.map((item, index) => {
            const Icon = navIconMap[item.id] || LayoutDashboard;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <motion.li
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.id}`}
                      onClick={onItemClick}
                      className={cn(
                        'group relative flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-xl min-h-[44px]',
                        'transition-[background-color,color,box-shadow] duration-200',
                        collapsed && 'justify-center px-2',
                        isActive
                          ? 'bg-ae-blue text-white font-semibold shadow-blue'
                          : 'text-slate-300/90 hover:text-white hover:bg-white/[0.07]'
                      )}
                    >
                      {isActive && !collapsed && (
                        <span aria-hidden className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-ae-cyan" />
                      )}
                      <Icon className={cn('w-[18px] h-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110', isActive ? 'text-white' : 'text-slate-400 group-hover:text-ae-cyan')} />
                      {!collapsed && (
                        <span className="font-body text-sm truncate">{item.label}</span>
                      )}
                    </Link>
                  </TooltipTrigger>
                  {collapsed && (
                    <TooltipContent side="right" sideOffset={10}>{item.label}</TooltipContent>
                  )}
                </Tooltip>
              </motion.li>
            );
          })}
        </ul>
      </TooltipProvider>
    </nav>
  );

  const Brand = ({ collapsed = false, withClose = false }) => (
    <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
      <div className="flex items-center gap-3 overflow-hidden">
        {/* v96 — Show the school's own logo when the school admin has
            uploaded one; falls back to the school-code initial tile. */}
        {currentSchool?.schoolLogo ? (
          <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 bg-white ring-1 ring-white/20 flex items-center justify-center" data-testid="school-logo-brand">
            <img
              src={currentSchool.schoolLogo}
              alt={`${currentSchool.schoolName || 'School'} logo`}
              className="h-full w-full object-contain"
            />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-heading font-bold text-sm shrink-0 bg-gradient-to-br from-ae-blue to-ae-cyan shadow-blue">
            {currentSchool?.schoolCode?.substring(0, 2) || 'SIP'}
          </div>
        )}
        {!collapsed && (
          <div className="overflow-hidden min-w-0">
            <p className="font-heading font-semibold text-sm text-white leading-tight line-clamp-2 break-words">
              {currentSchool?.schoolName || 'Student Intelligence Platform'}
            </p>
            <p className="text-[11px] uppercase tracking-[0.14em] text-ae-cyan/80 mt-0.5">{currentSchool?.schoolCode || 'SIP'}</p>
          </div>
        )}
      </div>
      {withClose && (
        <button
          type="button"
          onClick={closeMobileDrawer}
          aria-label="Close menu"
          data-testid="mobile-sidebar-close"
          className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-lg text-slate-300 hover:bg-white/10"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* ─── Desktop sidebar (≥lg) ─── */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={cn(
          'hidden lg:flex fixed left-0 top-0 h-screen z-40',
          'bg-ae-navy text-white',
          'border-r border-white/5',
          'flex-col shadow-xl'
        )}
        data-testid="sidebar"
      >
        <Brand collapsed={sidebarCollapsed} />
        <NavList collapsed={sidebarCollapsed} />
        <div className="p-3 border-t border-white/10">
          <button
            onClick={toggleSidebar}
            data-testid="sidebar-toggle"
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg min-h-[44px]',
              'text-slate-400 hover:text-white',
              'hover:bg-white/[0.07]',
              'transition-colors duration-200',
              sidebarCollapsed && 'justify-center px-2'
            )}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-body">Collapse</span>
              </>
            )}
          </button>
        </div>
      </motion.aside>

      {/* ─── Mobile drawer (<lg) ─── */}
      <AnimatePresence>
        {mobileDrawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-drawer" role="dialog" aria-modal="true">
            <motion.div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={closeMobileDrawer}
              data-testid="mobile-sidebar-backdrop"
            />
            <motion.aside
              className="absolute top-0 left-0 bottom-0 w-[84%] max-w-[300px] bg-ae-navy text-white shadow-2xl flex flex-col"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
            >
              <Brand withClose />
              <NavList onItemClick={closeMobileDrawer} />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
